/**
 * A table whose rows nest, with lazily loaded children.
 *
 * Not a mode of `DataTable`. Levels, expansion and roving focus do not belong
 * bolted onto a component eight other screens use, and `DataTable`'s sorting
 * and selection machinery is meaningless once rows have parents — sorting a
 * subtree by balance would either reorder children away from their parents or
 * silently sort only the top level.
 *
 * Focus lives on the ROW, not on the cells and not on the twisty. That is the
 * WAI-ARIA treegrid pattern and it is what makes ArrowRight/ArrowLeft mean
 * "expand / collapse" rather than "move one cell sideways". The twisty is
 * therefore a mouse target only: `tabIndex={-1}`, `aria-hidden`, with the row
 * carrying `aria-expanded`. A focusable twisty that duplicated the row's own
 * expanded state would announce the same fact twice.
 */

import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react'
import { Icon } from './Icon'
import { Skeleton, Spinner, StateBlock, cx } from './core'
import { useXScroll } from './useXScroll'

export interface TreeGridNode<Row> {
  key: string
  row: Row
  /** 0 at the root. */
  depth: number
  /** 1-based position among its siblings — `aria-posinset`. */
  index: number
  setSize: number
  hasChildren: boolean
  expanded: boolean
  loading: boolean
  /** Ancestor keys, root first, excluding this node. */
  path: string[]
  /** Last of its sibling set — draws the L-corner on the lineage guide. */
  isLast: boolean
}

export interface TreeGridColumn<Row> {
  id: string
  header: ReactNode
  /** The first column receives the twisty and the indent automatically. */
  cell: (node: TreeGridNode<Row>) => ReactNode
  align?: 'left' | 'right' | 'center'
  width?: number | string
  numeric?: boolean
  hideBelow?: number
  /** Header tooltip. Say which GRAIN the column is — own figure or subtree roll-up. */
  help?: string
}

export interface TreeGridProps<Row> {
  /** Top level. */
  roots: Row[]
  columns: TreeGridColumn<Row>[]
  rowKey: (row: Row) => string
  hasChildren: (row: Row) => boolean
  /** Loaded children for a key. `undefined` means "not fetched yet". */
  childrenOf: (key: string) => Row[] | undefined
  expanded: Set<string>
  onExpandedChange: (next: Set<string>) => void
  /** Fired when a row is expanded and `childrenOf` has nothing for it. */
  onLoadChildren?: (key: string, row: Row) => void
  /** Keys whose children are in flight. */
  loadingKeys?: Set<string>
  /** Enter / Space on a focused row. */
  onActivate?: (row: Row) => void
  /**
   * Extra rows under an expanded, fully loaded node — a roll-up line, a "no
   * downline" note, a per-branch error with its own retry. Rendered at
   * `depth + 1` and spanning the table, so one branch failing never takes the
   * tree down.
   */
  renderNodeFooter?: (node: TreeGridNode<Row>) => ReactNode
  /** Accessible name of the whole grid. */
  label: string
  /** Accessible name of a row — used for the expansion announcement. */
  rowLabel: (row: Row) => string
  density?: 'compact' | 'default' | 'relaxed'
  /** Omit to inherit Appearance → Table density. */
  loading?: boolean
  skeletonRows?: number
  empty?: ReactNode
  rowTone?: (row: Row) => 'loss' | 'warn' | 'highlight' | undefined
  /** Scroll this row into view and focus it once — the "show in tree" landing. */
  focusKey?: string
  maxHeight?: number | string
}

export function TreeGrid<Row>({
  roots,
  columns,
  rowKey,
  hasChildren,
  childrenOf,
  expanded,
  onExpandedChange,
  onLoadChildren,
  loadingKeys,
  onActivate,
  renderNodeFooter,
  label,
  rowLabel,
  density,
  loading = false,
  skeletonRows = 6,
  empty,
  rowTone,
  focusKey,
  maxHeight,
}: TreeGridProps<Row>) {
  const xs = useXScroll<HTMLDivElement>()
  const bodyRef = useRef<HTMLTableSectionElement>(null)
  const [cursor, setCursor] = useState(0)
  const [announcement, setAnnouncement] = useState('')

  /** Depth-first over what is actually VISIBLE — a collapsed node's children are not. */
  const visible = useMemo(() => {
    const out: TreeGridNode<Row>[] = []
    const walk = (rows: Row[], depth: number, path: string[]) => {
      rows.forEach((row, i) => {
        const key = rowKey(row)
        const open = expanded.has(key)
        const node: TreeGridNode<Row> = {
          key,
          row,
          depth,
          index: i + 1,
          setSize: rows.length,
          hasChildren: hasChildren(row),
          expanded: open,
          loading: loadingKeys?.has(key) ?? false,
          path,
          isLast: i === rows.length - 1,
        }
        out.push(node)
        if (open) {
          const kids = childrenOf(key)
          if (kids?.length) walk(kids, depth + 1, [...path, key])
        }
      })
    }
    walk(roots, 0, [])
    return out
  }, [roots, expanded, childrenOf, hasChildren, loadingKeys, rowKey])

  // Clamp rather than reset: rows appear and disappear as branches open and
  // close, and dumping focus back to the top every time would make the tree
  // unusable from a keyboard.
  useEffect(() => {
    setCursor((current) => Math.max(0, Math.min(current, visible.length - 1)))
  }, [visible.length])

  const focusRow = useCallback((index: number) => {
    const el = bodyRef.current?.querySelectorAll<HTMLTableRowElement>('tr[data-treerow]')[index]
    el?.focus()
    el?.scrollIntoView({ block: 'nearest' })
  }, [])

  const moveTo = (index: number) => {
    if (index < 0 || index >= visible.length) return
    setCursor(index)
    focusRow(index)
  }

  const expand = (node: TreeGridNode<Row>) => {
    if (!node.hasChildren || node.expanded) return
    const next = new Set(expanded)
    next.add(node.key)
    onExpandedChange(next)
    if (childrenOf(node.key) === undefined) onLoadChildren?.(node.key, node.row)
    const kids = childrenOf(node.key)
    setAnnouncement(
      kids ? `${rowLabel(node.row)} expanded, ${kids.length} children` : `${rowLabel(node.row)} expanding`,
    )
  }

  const collapse = (node: TreeGridNode<Row>) => {
    const next = new Set(expanded)
    next.delete(node.key)
    onExpandedChange(next)
    setAnnouncement(`${rowLabel(node.row)} collapsed`)
  }

  const onKeyDown = (event: ReactKeyboardEvent<HTMLTableRowElement>, index: number) => {
    const node = visible[index]
    if (!node) return
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        moveTo(index + 1)
        return
      case 'ArrowUp':
        event.preventDefault()
        moveTo(index - 1)
        return
      case 'ArrowRight':
        event.preventDefault()
        if (!node.hasChildren) return
        if (!node.expanded) {
          expand(node)
          return
        }
        // Already open: step into the first child — but not while it is still
        // loading, or focus lands on a row that is about to be replaced.
        if (!node.loading && visible[index + 1]?.depth === node.depth + 1) moveTo(index + 1)
        return
      case 'ArrowLeft': {
        event.preventDefault()
        if (node.expanded) {
          collapse(node)
          return
        }
        const parent = node.path[node.path.length - 1]
        if (!parent) return
        const target = visible.findIndex((n) => n.key === parent)
        if (target >= 0) moveTo(target)
        return
      }
      case 'Home':
        event.preventDefault()
        moveTo(0)
        return
      case 'End':
        event.preventDefault()
        moveTo(visible.length - 1)
        return
      case 'Enter':
      case ' ':
        // Only when the ROW itself has focus. A cell may hold a real link or
        // button — the member on a commission leaf does — and those keep their
        // own keyboard behaviour: preventDefault here would swallow the
        // synthesized click and the link would silently do nothing, which is
        // exactly the kind of failure a mouse test never catches.
        if (event.target !== event.currentTarget) return
        // Space must be prevented or the page scrolls under the operator.
        event.preventDefault()
        onActivate?.(node.row)
        return
      default:
    }
  }

  // The "show in tree" landing: focus the named row once it exists, so a link
  // from a filtered match list arrives with the keyboard already in the right
  // place rather than at the top of the tree.
  const landed = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (!focusKey || landed.current === focusKey) return
    const index = visible.findIndex((n) => n.key === focusKey)
    if (index < 0) return
    landed.current = focusKey
    setCursor(index)
    focusRow(index)
  }, [focusKey, visible, focusRow])

  const span = columns.length

  if (!loading && roots.length === 0) {
    return <div className="w7-tablewrap">{empty ?? <StateBlock icon="tree" title="Nothing to show" />}</div>
  }

  return (
    <div className="w7-tablewrap" ref={xs.ref} data-xscroll={xs.xScroll} style={{ maxHeight }}>
      <table
        className="w7-table w7-tree"
        role="treegrid"
        aria-label={label}
        data-density={density}
      >
        <caption className="w7-sr">{label}</caption>
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.id}
                style={{ width: column.width, textAlign: column.align ?? (column.numeric ? 'right' : 'left') }}
                data-hide-below={column.hideBelow}
                title={column.help}
              >
                {/* The info glyph, matching DataTable. A tree needs it MORE than
                    a flat table does: a column's grain changes with depth here —
                    a bet's own figure on a leaf, the roll-up on a branch — and
                    a `title` with nothing to hint at it is a disclosure nobody
                    discovers. */}
                {column.help ? (
                  <span
                    className="w7-row"
                    style={{
                      display: 'inline-flex',
                      gap: 4,
                      justifyContent:
                        (column.align ?? (column.numeric ? 'right' : 'left')) === 'right'
                          ? 'flex-end'
                          : undefined,
                    }}
                  >
                    {column.header}
                    <Icon name="info" size={12} style={{ opacity: 0.4 }} />
                  </span>
                ) : (
                  column.header
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody ref={bodyRef}>
          {loading
            ? Array.from({ length: skeletonRows }, (_, i) => (
                <tr key={`skeleton-${i}`} aria-hidden="true">
                  {columns.map((column) => (
                    <td key={column.id} data-hide-below={column.hideBelow}>
                      <Skeleton width={column.numeric ? '60%' : '80%'} />
                    </td>
                  ))}
                </tr>
              ))
            : visible.map((node, index) => {
                const tone = rowTone?.(node.row)
                const footer = node.expanded && !node.loading ? renderNodeFooter?.(node) : null
                return (
                  <Fragment key={node.key}>
                    <tr
                      data-treerow="true"
                      data-tone={tone}
                      data-highlight={tone === 'highlight' ? 'true' : undefined}
                      aria-level={node.depth + 1}
                      aria-posinset={node.index}
                      aria-setsize={node.setSize}
                      aria-expanded={node.hasChildren ? node.expanded : undefined}
                      aria-busy={node.loading || undefined}
                      tabIndex={index === cursor ? 0 : -1}
                      onFocus={() => setCursor(index)}
                      onKeyDown={(event) => onKeyDown(event, index)}
                    >
                      {columns.map((column, ci) => (
                        <td
                          key={column.id}
                          style={{
                            textAlign: column.align ?? (column.numeric ? 'right' : 'left'),
                            ...(ci === 0 ? depthVar(node.depth) : null),
                          }}
                          className={cx(column.numeric && 'w7-mono', ci === 0 && 'w7-tree-cell')}
                          data-hide-below={column.hideBelow}
                        >
                          {ci === 0 ? (
                            <span className="w7-tree-row">
                              {node.hasChildren ? (
                                <button
                                  type="button"
                                  className="w7-tree-twisty"
                                  tabIndex={-1}
                                  aria-hidden="true"
                                  onClick={(event) => {
                                    event.stopPropagation()
                                    if (node.expanded) collapse(node)
                                    else expand(node)
                                  }}
                                >
                                  {node.loading ? (
                                    <Spinner size={13} />
                                  ) : (
                                    <Icon name={node.expanded ? 'chevron-down' : 'chevron-right'} size={14} />
                                  )}
                                </button>
                              ) : (
                                // A spacer of the twisty's exact width, so
                                // usernames stay in one column at every depth.
                                <span className="w7-tree-twisty" aria-hidden="true" />
                              )}
                              {column.cell(node)}
                            </span>
                          ) : (
                            column.cell(node)
                          )}
                        </td>
                      ))}
                    </tr>
                    {node.expanded && node.loading ? (
                      <tr aria-hidden="true">
                        <td colSpan={span} className="w7-tree-cell" style={depthVar(node.depth + 1)}>
                          <span className="w7-tree-row">
                            <span className="w7-tree-twisty" />
                            <Skeleton width={180} />
                          </span>
                        </td>
                      </tr>
                    ) : null}
                    {footer ? (
                      <tr>
                        <td colSpan={span} className="w7-tree-cell" style={depthVar(node.depth + 1)}>
                          <span className="w7-tree-row">
                            <span className="w7-tree-twisty" />
                            {footer}
                          </span>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                )
              })}
        </tbody>
      </table>
      <span className="w7-sr" aria-live="polite">
        {announcement}
      </span>
    </div>
  )
}

/** Indent is a custom property so one CSS rule owns the step at every depth. */
const depthVar = (depth: number): CSSProperties => ({ ['--w7-depth' as string]: depth })
